import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import {
  HasPermissionDirective,
  PermissionService,
  WebSocketService,
} from '@ipg/ngx-frontend-toolset';
import { PermissionSyncService } from '../../services/permission-sync.service';
import { EMPTY, Observable, catchError, forkJoin, tap } from 'rxjs';
import { DataTableContainerComponent } from '../../components/data-table-container/data-table-container.component';
import { DialogService } from '../../components/dialog/dialog.service';
import { UserRole } from '../../models/user-role.model';
import {
  SupportSession,
  SupportSessionCode,
  SupportSessionService,
} from '../../services/support-session.service';
import { UserRoleService } from '../../services/user-role.service';
import { UnitService } from '../units/services/unit.service';
import { UnitResponseModel } from '../units/units.model';
import { CreateSupportCodeDialogComponent } from './components/create-support-code-dialog/create-support-code-dialog.component';

function formatDurationLabel(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const minText = (count: number) =>
    `${count} ${count === 1 ? 'minuut' : 'minuten'}`;

  if (hours === 0) {
    return minText(mins);
  }
  const hoursStr = `${hours} uur`;
  if (mins === 0) {
    return hoursStr;
  }
  return `${hoursStr} ${minText(mins)}`;
}

@Component({
  selector: 'app-settings-support-sessions',
  imports: [
    DatePipe,
    MatButton,
    MatIcon,
    MatIconButton,
    MatPaginator,
    MatTableModule,
    MatTooltip,
    DataTableContainerComponent,
    HasPermissionDirective,
  ],
  templateUrl: './settings-support-sessions.component.html',
  styleUrl: './settings-support-sessions.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsSupportSessionsComponent {
  private readonly _dialog = inject(DialogService);
  private readonly _matDialog = inject(MatDialog);
  private readonly _snackbar = inject(MatSnackBar);
  private readonly _supportSessionService = inject(SupportSessionService);
  private readonly _unitService = inject(UnitService);
  private readonly _userRoleService = inject(UserRoleService);
  private readonly _wsService = inject(WebSocketService);
  private readonly _permissionService = inject(PermissionService);
  private readonly _permissionSync = inject(PermissionSyncService);

  protected readonly codes = this._supportSessionService.codes;
  protected readonly sessions = this._supportSessionService.sessions;
  protected readonly units = signal<UnitResponseModel[]>([]);
  protected readonly roles = signal<UserRole[]>([]);

  /** Pagination signals for session history */
  protected readonly sessionPage = signal(0);
  protected readonly sessionPageSize = signal(5);

  /** Pagination signals for support access codes */
  protected readonly codePage = signal(0);
  protected readonly codePageSize = signal(5);

  protected readonly displayedColumns = [
    'created',
    'createdBy',
    'scope',
    'role',
    'duration',
    'status',
    'actions',
  ];

  protected readonly sessionColumns = [
    'agent',
    'reason',
    'scope',
    'role',
    'started',
    'duration',
    'status',
    'actions',
  ];

  protected readonly activeSessions = computed(() =>
    this.sessions().filter((s) => this.isLive(s))
  );
  protected readonly activeSessionsCount = computed(
    () => this.activeSessions().length
  );
  protected readonly hasNoData = computed(() => this.codes().length === 0);
  protected readonly hasNoSessions = computed(() => this.sessions().length === 0);

  /** Signal-native paginated slice for sessions */
  protected readonly paginatedSessions = computed(() => {
    const start = this.sessionPage() * this.sessionPageSize();
    return this.sessions().slice(start, start + this.sessionPageSize());
  });

  /** Signal-native paginated slice for access codes */
  protected readonly paginatedCodes = computed(() => {
    const start = this.codePage() * this.codePageSize();
    return this.codes().slice(start, start + this.codePageSize());
  });

  constructor() {
    this._supportSessionService.getCodes().subscribe();
    this._supportSessionService.getSessions().subscribe();

    this._wsService
      .connect('/event-stream', {
        requireAuth: true,
        topics: ['support-session/#'],
      })
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: (connection: any) => {
          connection.events$.subscribe(
            (event: { eventType?: string; payload?: unknown }) => {
              if (event.eventType?.startsWith('support_session')) {
                this._supportSessionService.getSessions().subscribe();
                this._supportSessionService.getCodes().subscribe();
              }
            }
          );
        },
      });
  }

  handleSessionPageChange({ pageIndex, pageSize }: PageEvent): void {
    this.sessionPageSize.set(pageSize);
    this.sessionPage.set(pageIndex);
  }

  handleCodePageChange({ pageIndex, pageSize }: PageEvent): void {
    this.codePageSize.set(pageSize);
    this.codePage.set(pageIndex);
  }

  openAddForm(): void {
    // A code grants the supporter one or more of the unit's roles, picked from
    // the role list, which needs `user_role:read` on top of creating codes.
    if (!this._permissionService.hasPermission('user_role:read')) {
      this._permissionSync.explainMissing(['user_role:read']);
      return;
    }

    forkJoin({
      units: this._unitService.getByUser(),
      roles: this._userRoleService.getList({
        params: { sortBy: 'label', sortOrder: 'asc' },
      }),
    }).subscribe({
      next: ({
        units,
        roles,
      }: {
        units: UnitResponseModel[];
        roles: UserRole[];
      }) => {
        this.units.set(units);
        this.roles.set(roles);
        const currentUnitId = this._unitService.currentUnit()?._id ?? '';
        const dialogRef = this._matDialog.open(CreateSupportCodeDialogComponent, {
          width: '560px',
          disableClose: true,
          data: {
            units,
            roles,
            currentUnitId,
          },
        });

        dialogRef
          .afterClosed()
          .subscribe((codeCreated: boolean | undefined) => {
            if (codeCreated) {
              this.codePage.set(0);
              this._supportSessionService.getCodes().subscribe();
            }
          });
      },
      error: () => {
        this._snackbar.open(
          'Kon gegevens voor supportcode niet laden.',
          'Sluiten',
          { duration: 4000 }
        );
      },
    });
  }

  getSessionDurationText(session: SupportSession): string {
    if (!session.createdAt) return '—';
    const start = new Date(session.createdAt).getTime();
    let end: number;

    if (session.endedAt) {
      end = new Date(session.endedAt).getTime();
    } else if (!this.isLive(session)) {
      end = new Date(session.expiresAt).getTime();
    } else {
      end = Date.now();
    }

    const diffMinutes = Math.round((end - start) / 60000);
    if (diffMinutes < 1) return '< 1 minuut';
    return formatDurationLabel(diffMinutes);
  }

  getDurationText(createdAt?: string, expiresAt?: string): string {
    if (!createdAt || !expiresAt) return '—';
    const start = new Date(createdAt).getTime();
    const end = new Date(expiresAt).getTime();
    const diffMinutes = Math.round((end - start) / 60000);
    if (diffMinutes <= 0) return '—';
    return formatDurationLabel(diffMinutes);
  }

  getInitials(name?: string): string {
    if (!name) return 'RC';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  getTimeAgoText(createdAt?: string): string {
    if (!createdAt) return '—';
    const start = new Date(createdAt).getTime();
    const now = Date.now();
    const diffMinutes = Math.max(0, Math.floor((now - start) / 60000));
    if (diffMinutes === 0) return 'zojuist';
    if (diffMinutes < 60) return `${diffMinutes} min. geleden`;
    const hours = Math.floor(diffMinutes / 60);
    const remainingMins = diffMinutes % 60;
    if (hours < 24) {
      return remainingMins > 0
        ? `${hours}u ${remainingMins}m geleden`
        : `${hours} uur geleden`;
    }
    const days = Math.floor(hours / 24);
    return `${days} dag(en) geleden`;
  }

  getRemainingTimeText(expiresAt?: string): string {
    if (!expiresAt) return '—';
    const end = new Date(expiresAt).getTime();
    const now = Date.now();
    const diffMinutes = Math.floor((end - now) / 60000);
    if (diffMinutes <= 0) return 'Verlopen';
    if (diffMinutes < 60) return `${diffMinutes} minuten`;
    const hours = Math.floor(diffMinutes / 60);
    const remainingMins = diffMinutes % 60;
    if (remainingMins === 0) return `${hours} uur`;
    return `${hours} uur ${remainingMins} min`;
  }

  handleRevoke(code: SupportSessionCode): void {
    this._dialog.confirm({
      title: 'Code intrekken',
      message:
        'Weet je zeker dat je deze code wilt intrekken? ' +
        'Support kan er daarna niet meer mee inloggen.',
      confirmText: 'Intrekken',
      cancelText: 'Annuleren',
      onConfirm: () => this._performRevoke(code.id),
    });
  }

  handleEnd(session: SupportSession): void {
    this._dialog.confirm({
      title: 'Sessie beëindigen',
      message:
        `Weet je zeker dat je de sessie van ${session.crmDisplayName} wilt ` +
        'beëindigen? De toegang wordt direct ingetrokken en support wordt ' +
        'uitgelogd.',
      confirmText: 'Beëindigen',
      cancelText: 'Annuleren',
      onConfirm: () => this._performEnd(session.id),
    });
  }

  /** How a session ended, or that it has not. */
  sessionStatusLabel(session: SupportSession): string {
    if (session.status === 'ended') return 'Beëindigd';
    if (session.status === 'expired') return 'Verlopen';
    return session.expired ? 'Verlopen' : 'Actief';
  }

  /** Whether support can still get in with this session. */
  isLive(session: SupportSession): boolean {
    return session.status === 'active' && !session.expired;
  }

  /** What a code still allows: usable, or spent in some way. */
  statusLabel(code: SupportSessionCode): string {
    if (code.status === 'redeemed') return 'Gebruikt';
    if (code.status === 'revoked') return 'Ingetrokken';
    return code.expired ? 'Verlopen' : 'Actief';
  }

  isUsable(code: SupportSessionCode): boolean {
    return code.status === 'active' && !code.expired;
  }

  private _performEnd(sessionId: string): Observable<SupportSession> {
    return this._supportSessionService.endSession(sessionId).pipe(
      tap(() => this._snackbar.open('Sessie beëindigd', 'OK')),
      catchError(() => {
        this._dialog.alert({
          title: 'Foutmelding',
          message: 'Er is iets misgegaan bij het beëindigen van de sessie.',
          confirmText: 'OK',
        });
        return EMPTY;
      })
    );
  }

  private _performRevoke(codeId: string): Observable<SupportSessionCode> {
    return this._supportSessionService.revokeCode(codeId).pipe(
      tap(() => this._snackbar.open('Code ingetrokken', 'OK')),
      catchError(() => {
        this._dialog.alert({
          title: 'Foutmelding',
          message: 'Er is iets misgegaan bij het intrekken van de code.',
          confirmText: 'OK',
        });
        return EMPTY;
      })
    );
  }
}
